import { useId, useMemo, useRef, useState } from 'react';
import { useSession } from '../state/session';
import { TagChip } from './TagChip';

interface TagEditorProps {
  tags: string[];
  onChange(tags: string[]): void;
  /** tags marked with a small "auto" label */
  autoTags?: readonly string[];
  /** tags marked with a small "album" label (the album's default tags) */
  albumTags?: readonly string[];
  placeholder?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
}

/**
 * Chip input (prototype `tagEditor`): Enter or comma adds, Backspace on empty removes the last tag. Suggestions are
 * every known tag except the ones already added here (aliases resolve to their tag, so they never suggest it again).
 */
export function TagEditor({
  tags,
  onChange,
  autoTags = [],
  albumTags = [],
  placeholder = 'Add tag…',
  className = 'tagin',
  id,
  disabled,
}: TagEditorProps) {
  const { tagIndex } = useSession();
  const [value, setValue] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const listId = `tags-${useId()}`;
  const suggestions = useMemo(() => [...tagIndex.registry.keys()].filter((t) => !tags.includes(t)), [tagIndex, tags]);

  const commit = () => {
    const t = tagIndex.normalize(value);
    setValue('');
    if (t && !tags.includes(t)) onChange([...tags, t]);
  };

  return (
    <div className={className} id={id} onClick={() => input.current?.focus()}>
      {tags.map((t) => (
        <TagChip
          key={t}
          tag={t}
          extra={albumTags.includes(t) ? <span className="auto"> album</span> : autoTags.includes(t) ? <span className="auto"> auto</span> : undefined}
          onRemove={disabled ? undefined : () => onChange(tags.filter((x) => x !== t))}
        />
      ))}
      <input
        ref={input}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        list={listId}
        aria-label="Add tag"
        onChange={(e) => {
          const v = e.target.value;
          // Picking a suggestion from the list replaces the text without typing (no InputEvent, or a replacement):
          // add it as a chip right away.
          const native = e.nativeEvent;
          const picked = typeof InputEvent === 'undefined' || !(native instanceof InputEvent) || native.inputType === 'insertReplacementText';
          const tag = picked ? tagIndex.normalize(v) : null;
          if (tag && tagIndex.registry.has(tag)) {
            setValue('');
            if (!tags.includes(tag)) onChange([...tags, tag]);
            return;
          }
          if (v.endsWith(',')) {
            const t = tagIndex.normalize(v.slice(0, -1));
            setValue('');
            if (t && !tags.includes(t)) onChange([...tags, t]);
          } else setValue(v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && value.trim()) {
            e.preventDefault();
            commit();
          } else if (e.key === 'Backspace' && !value && tags.length) {
            onChange(tags.slice(0, -1));
          }
        }}
        onBlur={() => {
          if (value.trim()) commit();
        }}
      />
      <datalist id={listId}>
        {suggestions.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
    </div>
  );
}
